import { useTimeoutPoll } from '@vueuse/core';
import { ref, readonly } from 'vue';

export interface UsePollOptions {
  /**
   * Return true to stop polling after fn resolves.
   * Called immediately after each invocation of fn.
   */
  isDone?: () => boolean;
  /**
   * Compute the delay (ms) before the next poll given the attempt index (0-based).
   * Called after each attempt to set the delay for the *next* one.
   * Defaults to `fixedDelay(1000)`.
   */
  delayStrategy?: (attempt: number) => number;
  /**
   * Maximum number of poll attempts before halting and marking as exhausted.
   * Defaults to Infinity (poll indefinitely until isDone returns true).
   */
  maxAttempts?: number;
  /**
   * Start polling immediately when the composable is mounted.
   * Defaults to false — call `resume()` to start.
   */
  immediate?: boolean;
}

export interface UsePollReturn {
  isActive: Readonly<ReturnType<typeof ref<boolean>>>;
  isExhausted: Readonly<ReturnType<typeof ref<boolean>>>;
  pause: () => void;
  /** Reset the attempt counter and start (or restart) polling from the beginning. */
  resume: () => void;
}

// ---------------------------------------------------------------------------
// Built-in delay strategies
// ---------------------------------------------------------------------------

const DEFAULT_FIXED_DELAY_MS = 1000;
const DEFAULT_EXPONENTIAL_INITIAL_DELAY_MS = 500;
const DEFAULT_EXPONENTIAL_BASE = 2;
const DEFAULT_EXPONENTIAL_MAX_EXPONENT = 5;

/**
 * Returns a fixed delay of `ms` milliseconds for every attempt.
 * Example: `fixedDelay(1000)`
 */
export function fixedDelay(ms: number): (attempt: number) => number {
  return () => ms;
}

/**
 * Returns an exponential backoff strategy.
 * delay(attempt) = initialDelay × base^min(attempt, maxExponent)
 *
 * Default values mirror the WorkflowRepository retry configuration:
 *   initialDelay=500, base=2, maxExponent=5 → caps at 500×2^5 = 16 000 ms
 *
 * Example: `exponentialBackoff()` or `exponentialBackoff(500, 2, 5)`
 */
export function exponentialBackoff(
  initialDelay = DEFAULT_EXPONENTIAL_INITIAL_DELAY_MS,
  base = DEFAULT_EXPONENTIAL_BASE,
  maxExponent = DEFAULT_EXPONENTIAL_MAX_EXPONENT,
): (attempt: number) => number {
  return (attempt: number) => initialDelay * Math.pow(base, Math.min(attempt, maxExponent));
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

/**
 * Generic polling composable backed by `useTimeoutPoll`.
 *
 * - Executes `fn` sequentially (waits for completion before scheduling the next call).
 * - Stops automatically when `isDone()` returns true or `maxAttempts` is reached.
 * - Delay between attempts is controlled by a swappable `delayStrategy`.
 *
 * @example
 * ```ts
 * const { isActive, isExhausted, resume, pause } = usePoll(
 *   () => subscriptionStore.fetchSubscriptions(ids),
 *   {
 *     isDone: () => subscriptionStore.selectedSubscription?.displayStatus === 'ACTIVE',
 *     delayStrategy: exponentialBackoff(),
 *     maxAttempts: 10,
 *   },
 * );
 *
 * // Start polling after a user action:
 * resume();
 * ```
 */
export function usePoll(fn: () => Promise<unknown>, options: UsePollOptions = {}): UsePollReturn {
  const {
    isDone,
    delayStrategy = fixedDelay(DEFAULT_FIXED_DELAY_MS),
    maxAttempts = Infinity,
    immediate = false,
  } = options;

  const attempt = ref(0);
  const isExhausted = ref(false);
  const currentDelay = ref(delayStrategy(0));

  const { isActive, pause, resume: internalResume } = useTimeoutPoll(
    async () => {
      await fn();

      if (isDone?.()) {
        pause();
        return;
      }

      attempt.value++;
      currentDelay.value = delayStrategy(attempt.value);

      if (attempt.value >= maxAttempts) {
        isExhausted.value = true;
        pause();
      }
    },
    currentDelay,
    { immediate, immediateCallback: true },
  );

  /** Reset state and start polling from the first attempt. */
  function resume(): void {
    attempt.value = 0;
    isExhausted.value = false;
    currentDelay.value = delayStrategy(0);
    internalResume();
  }

  return {
    isActive: readonly(isActive),
    isExhausted: readonly(isExhausted),
    pause,
    resume,
  };
}
